import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ThreadList } from '../../components/Thread/ThreadList';
import { ChatWindow } from '../../components/Thread/ChatWindow';
import { SystemChatWindow } from '../../components/Thread/SystemChatWindow';
import { ProposalDialog } from '../../components/Thread/ProposalDialog';
import { TrialRequestDialog, TrialRequestSubmitValue } from '../../components/Thread/TrialRequestDialog';
import { CreateContractModal } from '../../components/Thread/CreateContractModal';
import BriefDetailModal, { BriefDetailType } from '../../components/Profile/brief/BriefDetailModal';
import ServiceDetailModal, { ServiceDetailType } from '../../components/Profile/service/ServiceDetailModal';
import { CURRENT_USER_ID } from '../../data/mockContracts';
import { Thread, ThreadMessage, ProposalPayload, InvitationPayload, TrialRequestPayload, TrialRequestStatus } from '../../types/thread';
import { CreateContractInput } from '../../types/contract';
import { contractService } from '../../services/contractService';
import { briefService } from '../../services/briefService';
import { userService } from '../../services/userService';
import { timService } from '../../services/timService';
import { chatApiService, ChatHandoff, ChatTranscriptEntry, ChatUserSummary } from '../../services/chatApiService';
import { timConversationToThread, timMessageToThreadMessage } from '../../utils/timAdapters';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import { useConfirm, useToast } from '../../components/UI/Toast';
import { translateBackendError } from '../../utils/translateBackendError';

const getOppositeThreadRole = (role: 'CLIENT' | 'CREATOR'): 'CLIENT' | 'CREATOR' => (
    role === 'CREATOR' ? 'CLIENT' : 'CREATOR'
);

const resolveThreadRole = (role?: string): 'CLIENT' | 'CREATOR' => {
    const normalized = String(role || '').toLowerCase();
    return normalized === 'creator' || normalized === 'both' ? 'CREATOR' : 'CLIENT';
};

const applyTrialRequestStatusSync = (messageList: ThreadMessage[]): ThreadMessage[] => {
    const nextMessages = [...messageList];

    const updateTrialRequestAt = (index: number, status: TrialRequestStatus) => {
        const targetMessage = nextMessages[index];
        if (!targetMessage || targetMessage.type !== 'TRIAL_REQUEST' || !targetMessage.payload) {
            return;
        }

        const trialPayload = targetMessage.payload as TrialRequestPayload;
        if (trialPayload.status === status) {
            return;
        }

        if (typeof window !== 'undefined') {
            localStorage.setItem(`trial_status_${targetMessage.id}`, status);
            if (trialPayload.trialId) {
                localStorage.setItem(`trial_status_by_trial_${trialPayload.trialId}`, status);
            }
        }

        nextMessages[index] = {
            ...targetMessage,
            payload: {
                ...trialPayload,
                status
            }
        };
    };

    const inferStatusFromSystemMessage = (message: ThreadMessage): TrialRequestStatus | undefined => {
        const payload = message.payload as any;
        if (payload?.trialRequestStatus) {
            return payload.trialRequestStatus as TrialRequestStatus;
        }

        if (message.content.includes('已接受试稿') || message.content.includes('试稿已开始')) {
            return 'ACCEPTED';
        }

        if (message.content.includes('已拒绝试稿请求')) {
            return 'REJECTED';
        }

        return undefined;
    };

    for (let index = 0; index < nextMessages.length; index += 1) {
        const message = nextMessages[index];
        if (message.type !== 'SYSTEM') {
            continue;
        }

        const syncedStatus = inferStatusFromSystemMessage(message);
        if (!syncedStatus) {
            continue;
        }

        const payload = message.payload as any;
        const targetTrialId = payload?.trialId ? String(payload.trialId) : '';

        if (targetTrialId) {
            const trialIndex = nextMessages.findIndex((candidate) => (
                candidate.type === 'TRIAL_REQUEST'
                && !!candidate.payload
                && String((candidate.payload as TrialRequestPayload).trialId || '') === targetTrialId
            ));

            if (trialIndex >= 0) {
                updateTrialRequestAt(trialIndex, syncedStatus);
                continue;
            }
        }

        for (let fallbackIndex = index - 1; fallbackIndex >= 0; fallbackIndex -= 1) {
            const candidate = nextMessages[fallbackIndex];
            if (candidate.type === 'TRIAL_REQUEST' && candidate.payload) {
                updateTrialRequestAt(fallbackIndex, syncedStatus);
                break;
            }
        }
    }

    return nextMessages;
};

const Messages = () => {
    const { id: routeThreadId } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast, ToastWrapper } = useToast();
    const { confirm, ConfirmWrapper } = useConfirm();

    // State
    const SYSTEM_THREAD_ID = '__system__';
    const sentContextRef = useRef<Set<string>>(new Set());
    const [threads, setThreads] = useState<Thread[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(routeThreadId || null);
    const [messages, setMessages] = useState<ThreadMessage[]>([]);
    const [isProposalDialogOpen, setIsProposalDialogOpen] = useState(false);
    const [proposalType, setProposalType] = useState<'REQUEST' | 'SUBMIT'>('SUBMIT');
    const [proposalInitialData, setProposalInitialData] = useState<ProposalPayload | undefined>(undefined);
    const [proposalReadOnly, setProposalReadOnly] = useState(false);

    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    const [contractInitialData, setContractInitialData] = useState<Partial<CreateContractInput> | undefined>(undefined);

    const [isBriefModalOpen, setIsBriefModalOpen] = useState(false);
    const [selectedBrief, setSelectedBrief] = useState<BriefDetailType | null>(null);

    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const [selectedService, setSelectedService] = useState<ServiceDetailType | null>(null);
    const [isTrialDialogOpen, setIsTrialDialogOpen] = useState(false);
    const [trialDialogDefaultBriefId, setTrialDialogDefaultBriefId] = useState<string | undefined>(undefined);
    const [actualCurrentUserId, setActualCurrentUserId] = useState<string>(CURRENT_USER_ID);
    const [currentUserName, setCurrentUserName] = useState<string>('Me');
    const [currentUserAvatar, setCurrentUserAvatar] = useState<string>('');
    const [currentUserRole, setCurrentUserRole] = useState<'CLIENT' | 'CREATOR'>('CLIENT');
    const [isCurrentUserOfficial, setIsCurrentUserOfficial] = useState(false);
    const [officialUser, setOfficialUser] = useState<ChatUserSummary | null>(null);
    const [handoffs, setHandoffs] = useState<ChatHandoff[]>([]);
    const [handoffTranscripts, setHandoffTranscripts] = useState<Record<string, ChatTranscriptEntry[]>>({});
    const [loadingTranscriptIds, setLoadingTranscriptIds] = useState<Set<string>>(new Set());
    const [timReady, setTimReady] = useState(timService.isReady());
    const partnerThreadRole = getOppositeThreadRole(currentUserRole);
    const systemThread: Thread = useMemo(() => ({
        id: SYSTEM_THREAD_ID,
        participants: [
            { userId: actualCurrentUserId, nickname: currentUserName, avatar: currentUserAvatar, role: currentUserRole, lastReadAt: Date.now() },
            { userId: 'system', nickname: '系统消息', avatar: '', role: partnerThreadRole, lastReadAt: Date.now() }
        ],
        category: 'GENERAL' as const,
        unreadCount: 0,
        updatedAt: Date.now(),
    }), [SYSTEM_THREAD_ID, actualCurrentUserId, currentUserAvatar, currentUserName, currentUserRole, partnerThreadRole]);

    const activeThread = activeThreadId ? threads.find((thread) => thread.id === activeThreadId) ?? null : null;
    const activeThreadCurrentUserRole = activeThread?.participants.find((participant) => participant.userId === actualCurrentUserId)?.role;
    const activeThreadPartner = activeThread?.participants.find((participant) => participant.userId !== actualCurrentUserId);
    const activeHandoff = activeThreadId
        ? handoffs.find((handoff) => handoff.officialConversationId === activeThreadId && handoff.status === 'ACTIVE')
        : undefined;

    const loadHandoffs = useCallback(async () => {
        try {
            const nextHandoffs = await chatApiService.listHandoffs();
            setHandoffs(nextHandoffs);
            return nextHandoffs;
        } catch (error) {
            console.error('Failed to load chat handoffs:', error);
            return [];
        }
    }, []);

    const buildOfficialHandoffThread = useCallback((handoff: ChatHandoff): Thread => ({
        id: handoff.officialConversationId,
        participants: [
            {
                userId: actualCurrentUserId,
                nickname: currentUserName,
                avatar: currentUserAvatar,
                role: currentUserRole,
                lastReadAt: Date.now()
            },
            {
                userId: handoff.clientUserId,
                nickname: handoff.client?.displayName || handoff.client?.username || handoff.clientUserId,
                avatar: handoff.client?.avatarUrl || '',
                role: 'CLIENT',
                lastReadAt: Date.now()
            }
        ],
        category: 'GENERAL',
        unreadCount: 0,
        updatedAt: handoff.updatedAt,
        lastMessage: {
            id: `handoff_${handoff.handoffId}`,
            threadId: handoff.officialConversationId,
            senderId: handoff.creatorUserId,
            content: '创作者已接入官方，请查看交接记录。',
            type: 'SYSTEM',
            createdAt: handoff.createdAt,
            isRead: true
        }
    }), [actualCurrentUserId, currentUserAvatar, currentUserName, currentUserRole]);

    const mergeOfficialHandoffThreads = useCallback((baseThreads: Thread[], nextHandoffs: ChatHandoff[]): Thread[] => {
        if (!isCurrentUserOfficial) {
            return baseThreads;
        }

        const merged = [...baseThreads];
        nextHandoffs
            .filter((handoff) => handoff.status === 'ACTIVE')
            .forEach((handoff) => {
                const existingIndex = merged.findIndex((thread) => thread.id === handoff.officialConversationId);
                if (existingIndex >= 0) {
                    const existing = merged[existingIndex];
                    const partner = existing.participants.find((participant) => participant.userId !== actualCurrentUserId);
                    if (partner) {
                        partner.nickname = handoff.client?.displayName || handoff.client?.username || partner.nickname;
                        partner.avatar = handoff.client?.avatarUrl || partner.avatar;
                    }
                    existing.updatedAt = Math.max(existing.updatedAt, handoff.updatedAt);
                    return;
                }

                merged.push(buildOfficialHandoffThread(handoff));
            });

        return merged.sort((left, right) => right.updatedAt - left.updatedAt);
    }, [actualCurrentUserId, buildOfficialHandoffThread, isCurrentUserOfficial]);

    // Wait for TIM to be ready
    useEffect(() => {
        if (timReady) return;
        const interval = setInterval(() => {
            if (timService.isReady()) {
                setTimReady(true);
                clearInterval(interval);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [timReady]);

    // Load TIM conversations
    useEffect(() => {
        if (!timReady) return;
        let isActive = true;

        const loadConversations = async () => {
            try {
                const convList = await timService.getConversationList();
                const timThreads = convList.map((c: any) => timConversationToThread(c, actualCurrentUserId, currentUserRole));
                // Enrich partner profiles from our backend
                const partnerIds = timThreads
                    .map(t => t.participants.find(p => p.userId !== actualCurrentUserId)?.userId)
                    .filter(Boolean) as string[];
                if (partnerIds.length > 0) {
                    try {
                        const profileRes = await userService.getAvatarsBatch(partnerIds);
                        if (profileRes.success && profileRes.data?.avatars) {
                            const avatars = profileRes.data.avatars;
                            timThreads.forEach(t => {
                                const partner = t.participants.find(p => p.userId !== actualCurrentUserId);
                                if (partner && avatars[partner.userId]) {
                                    const info = avatars[partner.userId];
                                    partner.nickname = info.nickname || partner.userId;
                                    partner.avatar = info.avatarUrl || '';
                                }
                            });
                        }
                    } catch { /* ignore profile fetch errors */ }
                }
                // Restore saved context (category, referenceId, roles) for each thread
                timThreads.forEach(t => {
                    try {
                        const saved = JSON.parse(localStorage.getItem(`thread_ctx_${t.id}`) || '{}');
                        if (saved.category) {
                            t.category = saved.category;
                            t.referenceId = saved.referenceId;
                            t.returnTo = saved.returnTo;
                            const me = t.participants.find(p => p.userId === actualCurrentUserId);
                            const partner = t.participants.find(p => p.userId !== actualCurrentUserId);
                            if (me) me.role = currentUserRole;
                            if (partner) partner.role = getOppositeThreadRole(currentUserRole);
                        }
                    } catch { /* ignore */ }
                });
                const nextHandoffs = await loadHandoffs();
                const mergedThreads = mergeOfficialHandoffThreads([systemThread, ...timThreads], nextHandoffs);
                if (isActive) {
                    // Preserve any locally-added placeholder threads (e.g. user
                    // tapped 聊一聊 on a profile and we created a placeholder
                    // before TIM has a real conversation for them). Without
                    // this, loadConversations would clobber the placeholder
                    // and the chat window would render "Conversation not found"
                    // until the user sends the first message.
                    setThreads(prev => {
                        const knownIds = new Set(mergedThreads.map(t => t.id));
                        const orphans = prev.filter(t => t.id.startsWith('C2C') && !knownIds.has(t.id));
                        return orphans.length > 0 ? [...mergedThreads, ...orphans] : mergedThreads;
                    });
                }
            } catch (e) {
                console.error('Failed to load TIM conversations:', e);
            }
        };

        void loadConversations();
        const unsubscribeConversationUpdated = timService.onConversationUpdated(() => {
            void loadConversations();
        });

        return () => {
            isActive = false;
            unsubscribeConversationUpdated();
        };
    }, [actualCurrentUserId, currentUserAvatar, currentUserName, currentUserRole, isCurrentUserOfficial, loadHandoffs, mergeOfficialHandoffThreads, systemThread, timReady]);

    // Listen for incoming messages (exclude own messages — already shown optimistically)
    useEffect(() => {
        const unsubscribeMessageReceived = timService.onMessageReceived((msgs: any[]) => {
            msgs.forEach(msg => {
                if (msg.conversationID === activeThreadId && msg.from !== actualCurrentUserId) {
                    const converted = timMessageToThreadMessage(msg);
                    setMessages(prev => applyTrialRequestStatusSync([...prev, converted]));
                }
            });
        });

        return () => {
            unsubscribeMessageReceived();
        };
    }, [activeThreadId, actualCurrentUserId]);

    useEffect(() => {
        // When the URL drops the :id segment (e.g. user tapped the
        // back-to-list button on mobile), clear the active thread so
        // the layout swaps from chat-pane back to list-pane. Without
        // this clear, activeThreadId would stay stuck on the previous
        // thread and the mobile single-pane layout would never show
        // the list.
        if (!routeThreadId) {
            setActiveThreadId(null);
            return;
        }
        setActiveThreadId(routeThreadId);
        if (routeThreadId === SYSTEM_THREAD_ID) return;

        // Read context from nav state or localStorage
        const navState = location.state as { category?: string; referenceId?: string; returnTo?: string; referenceTitle?: string; referenceDetails?: any } | null;
        const storageKey = `thread_ctx_${routeThreadId}`;
        let threadCategory: Thread['category'] = 'GENERAL';
        let threadReferenceId: string | undefined;
        let threadReturnTo: string | undefined;

        if (navState?.category) {
            threadCategory = navState.category as Thread['category'];
            threadReferenceId = navState.referenceId;
            threadReturnTo = navState.returnTo;
            // Persist for refresh
            localStorage.setItem(storageKey, JSON.stringify({ category: threadCategory, referenceId: threadReferenceId, returnTo: threadReturnTo, referenceTitle: navState.referenceTitle }));

            // Send context message to thread when initiated from service/brief (only once per session)
            if (threadReferenceId && timReady) {
                const sentKey = `${routeThreadId}_${threadReferenceId}`;
                if (!sentContextRef.current.has(sentKey)) {
                    sentContextRef.current.add(sentKey);
                    const details = navState.referenceDetails || {};
                    const contextMsg: ThreadMessage = {
                        id: `ctx_${Date.now()}`,
                        threadId: routeThreadId,
                        senderId: actualCurrentUserId,
                        content: navState.referenceTitle || (threadCategory === 'BRIEF' ? '需求参考' : '服务参考'),
                        type: 'INVITATION',
                        payload: {
                            text: `我想就以下${threadCategory === 'BRIEF' ? '需求' : '服务'}与您沟通`,
                            title: details.title || navState.referenceTitle,
                            description: details.description,
                            budgetRange: details.budgetRange,
                            priceRange: details.priceRange,
                            deliveryTimeRange: details.deliveryTimeRange,
                            tags: details.tags,
                            deadline: details.deadline,
                            briefId: threadCategory === 'BRIEF' ? threadReferenceId : undefined,
                            serviceId: threadCategory === 'SERVICE' ? threadReferenceId : undefined,
                        } as any,
                        createdAt: Date.now(),
                        isRead: true,
                    };
                    // Show immediately to sender
                    appendMessageToThread(routeThreadId, contextMsg);
                    // Send via TIM to receiver
                    timService.sendCustomMessage(routeThreadId, {
                        type: contextMsg.type,
                        content: contextMsg.content,
                        payload: contextMsg.payload,
                    }).catch(console.error);
                }
            }
        } else {
            try {
                const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
                threadCategory = saved.category || 'GENERAL';
                threadReferenceId = saved.referenceId;
                threadReturnTo = saved.returnTo;
            } catch { /* ignore */ }
        }

        // Roles: current user is always CLIENT perspective, partner is CREATOR
        // ChatWindow swaps shortcuts based on partner.role
        const myRole = currentUserRole;
        const partnerRole = getOppositeThreadRole(currentUserRole);

        // Ensure thread exists in list (for new conversations).
        // Wait for the real userId to resolve from /me — otherwise the
        // sentinel CURRENT_USER_ID lands in participants[0] and the chat
        // header treats *that* as the partner (showing "Me").
        if (routeThreadId.startsWith('C2C') && actualCurrentUserId !== CURRENT_USER_ID && !threads.find(t => t.id === routeThreadId)) {
            const partnerId = routeThreadId.replace('C2C', '');
            // Fetch partner profile for display name
            userService.getUserProfileById(partnerId).then(res => {
                const profile = res.data?.userProfile;
                const partnerNickname = profile?.displayName || profile?.username || partnerId;
                const partnerAvatar = profile?.avatarUrl || '';
                const placeholder: Thread = {
                    id: routeThreadId,
                    participants: [
                        { userId: actualCurrentUserId, nickname: currentUserName, avatar: currentUserAvatar, role: myRole as 'CLIENT' | 'CREATOR', lastReadAt: Date.now() },
                        { userId: partnerId, nickname: partnerNickname, avatar: partnerAvatar, role: partnerRole as 'CLIENT' | 'CREATOR', lastReadAt: Date.now() },
                    ],
                    category: threadCategory,
                    referenceId: threadReferenceId,
                    returnTo: threadReturnTo,
                    unreadCount: 0,
                    updatedAt: Date.now(),
                };
                setThreads(prev => {
                    // If loadConversations raced us and already inserted a row
                    // with empty profile data, merge our resolved nickname/avatar
                    // in instead of bailing out.
                    const existing = prev.find(t => t.id === routeThreadId);
                    if (!existing) return [...prev, placeholder];
                    return prev.map(t => {
                        if (t.id !== routeThreadId) return t;
                        return {
                            ...t,
                            participants: t.participants.map(p => {
                                if (p.userId !== partnerId) return p;
                                const needsName = !p.nickname || p.nickname === partnerId;
                                const needsAvatar = !p.avatar;
                                return {
                                    ...p,
                                    nickname: needsName ? partnerNickname : p.nickname,
                                    avatar: needsAvatar ? partnerAvatar : p.avatar,
                                };
                            }),
                        };
                    });
                });
            }).catch(() => {
                setThreads(prev => {
                    if (prev.find(t => t.id === routeThreadId)) return prev;
                    return [...prev, {
                        id: routeThreadId,
                        participants: [
                            { userId: actualCurrentUserId, nickname: currentUserName, avatar: currentUserAvatar, role: myRole as 'CLIENT' | 'CREATOR', lastReadAt: Date.now() },
                            { userId: partnerId, nickname: partnerId, avatar: '', role: partnerRole as 'CLIENT' | 'CREATOR', lastReadAt: Date.now() },
                        ],
                        category: threadCategory,
                        referenceId: threadReferenceId,
                        returnTo: threadReturnTo,
                        unreadCount: 0,
                        updatedAt: Date.now(),
                    }];
                });
            });
        }

        if (timReady) {
            timService.getMessageList(routeThreadId).then(res => {
                setMessages(applyTrialRequestStatusSync(res.messages.map(timMessageToThreadMessage)));
            }).catch(console.error);
            timService.setMessageRead(routeThreadId).catch(console.error);
        }
    }, [routeThreadId, actualCurrentUserId, currentUserAvatar, currentUserName, currentUserRole, timReady]);

    useEffect(() => {
        let isActive = true;

        Promise.all([
            userService.getUserProfile(),
            chatApiService.getOfficialInfo().catch((error) => {
                console.error('Failed to load official chat info:', error);
                return null;
            })
        ])
            .then(([response, officialInfo]) => {
                const resolvedUserId = response.data?.userProfile?.userId;
                if (isActive && resolvedUserId) {
                    const resolvedName = response.data!.userProfile.displayName || response.data!.userProfile.username || resolvedUserId;
                    const resolvedAvatar = response.data!.userProfile.avatarUrl || '';
                    setActualCurrentUserId(resolvedUserId);
                    setCurrentUserName(resolvedName);
                    setCurrentUserAvatar(resolvedAvatar);
                    setCurrentUserRole(resolveThreadRole(response.data!.userProfile.role));
                    setIsCurrentUserOfficial(Boolean(response.data!.userProfile.isOfficial || officialInfo?.isCurrentUserOfficial));
                    if (officialInfo?.officialUser) {
                        setOfficialUser(officialInfo.officialUser);
                    }
                    // Rewrite any threads built before /me resolved — their
                    // "current user" participant still has the sentinel id
                    // which makes the chat header pick it as the partner.
                    setThreads(prev => prev.map(t => ({
                        ...t,
                        participants: t.participants.map(p => p.userId === CURRENT_USER_ID
                            ? { ...p, userId: resolvedUserId, nickname: resolvedName, avatar: resolvedAvatar }
                            : p),
                    })));
                }
            })
            .catch((error) => {
                console.error('Failed to resolve current user profile for messages:', error);
            });

        return () => {
            isActive = false;
        };
    }, []);

    const handleSelectThread = (threadId: string) => {
        if (threadId === activeThreadId) {
            return;
        }
        const selectedThread = threads.find((thread) => thread.id === threadId);
        navigate(`/messages/${threadId}`, {
            replace: true
        });
    };

    const handleBack = () => {
        const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const updateThreadWithLastMessage = (threadId: string, message: ThreadMessage) => {
        setThreads((prevThreads) => {
            const threadIndex = prevThreads.findIndex((thread) => thread.id === threadId);
            if (threadIndex === -1) {
                return prevThreads;
            }

            const updatedThreads = [...prevThreads];
            updatedThreads[threadIndex] = {
                ...updatedThreads[threadIndex],
                lastMessage: message,
                updatedAt: Date.now()
            };
            updatedThreads.sort((a, b) => b.updatedAt - a.updatedAt);
            return updatedThreads;
        });
    };

    const appendMessageToThread = (threadId: string, message: ThreadMessage) => {
        setMessages((prevMessages) => {
            if (threadId !== activeThreadId) {
                return prevMessages;
            }
            return [...prevMessages, message];
        });
        updateThreadWithLastMessage(threadId, message);
    };

    const sendStructuredMessage = async (threadId: string, message: ThreadMessage) => {
        appendMessageToThread(threadId, message);
        try {
            await timService.sendCustomMessage(threadId, {
                type: message.type,
                content: message.content,
                payload: message.payload,
            });
        } catch (e) {
            console.error('Failed to send structured message:', e);
        }
    };

    const loadHandoffTranscript = async (handoffId: string) => {
        if (handoffTranscripts[handoffId] || loadingTranscriptIds.has(handoffId)) {
            return;
        }

        setLoadingTranscriptIds((prev) => new Set(prev).add(handoffId));
        try {
            const transcript = await chatApiService.getHandoffTranscript(handoffId);
            setHandoffTranscripts((prev) => ({
                ...prev,
                [handoffId]: transcript
            }));
        } catch (error) {
            console.error('Failed to load handoff transcript:', error);
            showToast('加载交接记录失败', 'error');
        } finally {
            setLoadingTranscriptIds((prev) => {
                const next = new Set(prev);
                next.delete(handoffId);
                return next;
            });
        }
    };

    const buildTranscriptFromThread = async (threadId: string): Promise<ChatTranscriptEntry[]> => {
        const rawMessages = await timService.getFullMessageList(threadId);
        const participants = threads.find((thread) => thread.id === threadId)?.participants || [];

        return rawMessages
            .map(timMessageToThreadMessage)
            .filter((message) => message.content || message.payload)
            .map((message) => {
                const participant = participants.find((item) => item.userId === message.senderId);
                return {
                    id: message.id,
                    senderId: message.senderId,
                    senderName: participant?.nickname || message.senderId,
                    type: message.type,
                    content: message.content,
                    payload: message.payload,
                    createdAt: message.createdAt
                };
            });
    };

    const handleSendMessage = async (content: string) => {
        if (!activeThreadId) return;

        // Optimistic: show message immediately
        const optimisticMsg: ThreadMessage = {
            id: `local_${Date.now()}`,
            threadId: activeThreadId,
            senderId: actualCurrentUserId,
            content,
            type: 'TEXT',
            createdAt: Date.now(),
            isRead: true,
        };
        appendMessageToThread(activeThreadId, optimisticMsg);

        try {
            const res = await timService.sendTextMessage(activeThreadId, content);
        } catch (e) {
            console.error('Failed to send message:', e);
            showToast('发送失败', 'error');
        }
    };

    const updateTrialRequestStatus = (threadId: string, messageId: string, status: TrialRequestStatus) => {
        localStorage.setItem(`trial_status_${messageId}`, status);
        
        const updateMessage = (message: ThreadMessage) => {
            if (message.id !== messageId || message.type !== 'TRIAL_REQUEST' || !message.payload) {
                return message;
            }

            const trialPayload = message.payload as TrialRequestPayload;
            if (trialPayload.trialId) {
                localStorage.setItem(`trial_status_by_trial_${trialPayload.trialId}`, status);
            }

            return {
                ...message,
                payload: {
                    ...trialPayload,
                    status
                }
            };
        };

        setMessages((prevMessages) => applyTrialRequestStatusSync(prevMessages.map(updateMessage)));
    };

    const handleTrialRequestSubmit = ({ brief, trialFeeAmount, reviewSlaHours }: TrialRequestSubmitValue) => {
        if (!activeThreadId) {
            return;
        }

        const trialPayload: TrialRequestPayload = {
            trialId: `trial_${Date.now()}`,
            briefId: brief.id,
            briefTitle: brief.title,
            budgetRange: brief.budgetRange,
            ...(trialFeeAmount ? { trialFeeAmount } : {}),
            ...(reviewSlaHours ? { reviewSlaHours } : {}),
            initiatedByUserId: actualCurrentUserId,
            status: 'PENDING'
        };

        const newMessage: ThreadMessage = {
            id: `trial_${Date.now()}`,
            threadId: activeThreadId,
            senderId: actualCurrentUserId,
            content: `发起试稿：${brief.title}`,
            type: 'TRIAL_REQUEST',
            payload: trialPayload,
            createdAt: Date.now(),
            isRead: true
        };

        sendStructuredMessage(activeThreadId, newMessage);
        setIsTrialDialogOpen(false);
        setTrialDialogDefaultBriefId(undefined);
        showToast(trialFeeAmount ? '付费试稿请求已发送，等待对方接受' : '试稿请求已发送，等待对方接受', 'success');
    };

    const handleAction = async (action: string, payload?: any) => {
        console.log('Action triggered:', action, payload);
        if (action === 'SEND_SMS_REMINDER') {
            console.log('[SMS_REMINDER] handleAction received', {
                payload,
                actualCurrentUserId,
                activeThreadId,
            });
            const partnerUserId: string | undefined = payload?.partnerUserId;
            if (!partnerUserId) {
                console.error('[SMS_REMINDER] missing partnerUserId in payload');
                showToast('未找到对方用户，无法发送提醒', 'error');
                return;
            }
            try {
                const result = await chatApiService.sendSmsReminder(partnerUserId);
                if (result.success) {
                    console.log('[SMS_REMINDER] backend confirmed send');
                    showToast('已发送短信提醒', 'success');
                } else {
                    console.error('[SMS_REMINDER] backend rejected', result.error);
                    showToast(translateBackendError(result.error, '短信发送失败，请稍后重试'), 'error');
                }
            } catch (err: any) {
                console.error('[SMS_REMINDER] request error', err);
                showToast(translateBackendError(err?.message, '短信发送失败，请稍后重试'), 'error');
            }
            return;
        }
        if (action === 'START_COOPERATION') {
            // Find the last proposal in the current thread
            let proposalData: ProposalPayload | undefined = undefined;

            // First check if payload was provided (from clicking on a specific proposal)
            if (payload) {
                proposalData = payload as ProposalPayload;
            } else {
                // Otherwise, find the last proposal message in the thread
                const proposalMessages = messages.filter(m => m.type === 'PROPOSAL');
                if (proposalMessages.length > 0) {
                    const lastProposal = proposalMessages[proposalMessages.length - 1];
                    proposalData = lastProposal.payload as ProposalPayload;
                }
            }

            const thread = threads.find(t => t.id === activeThreadId);
            // The client is the OTHER person in the thread (partner)
            const clientUserId = thread?.participants.find(p => p.userId !== actualCurrentUserId)?.userId || '';
            const briefId = thread?.category === 'BRIEF' ? thread.referenceId : undefined;
            const partnerName = thread?.participants.find(p => p.userId !== actualCurrentUserId)?.nickname || '';

            // Generate default title: use brief title if available, else partner name
            let defaultTitle = partnerName ? `${partnerName} 合作合同` : '合作合同';
            if (briefId) {
                try {
                    const { briefService } = await import('../../services/briefService');
                    const res = await briefService.getBriefById(briefId);
                    const raw = res.data as any;
                    const brief = raw?.data?.brief || raw?.brief || raw;
                    if (brief?.title) defaultTitle = `${brief.title} 合同`;
                } catch { /* ignore, use fallback */ }
            }

            // Set contract initial data (empty if no proposal found)
            if (proposalData) {
                setContractInitialData({
                    client_user_id: clientUserId,
                    brief_id: briefId,
                    title: defaultTitle,
                    description: proposalData.description,
                    total_amount: proposalData.budget,
                    currency: proposalData.currency as any,
                    estimated_delivery_date: proposalData.deliveryDate,
                    milestones: proposalData.milestones
                });
            } else {
                // Open empty contract form but fill client and brief id
                setContractInitialData({
                    client_user_id: clientUserId,
                    brief_id: briefId,
                });
            }
            setIsContractModalOpen(true);
        } else if (action === 'REQUEST_PROPOSAL') {
            if (!activeThreadId) return;
            const invite = payload as any;
            const invitationPayload: InvitationPayload = {
                text: `希望您能为这个需求提交一份方案：${invite.title || ''}`,
                briefId: invite.briefId,
                isProposalRequest: true,
            } as any;
            const msg: ThreadMessage = {
                id: `msg_${Date.now()}`,
                threadId: activeThreadId,
                senderId: actualCurrentUserId,
                content: '请求方案',
                type: 'INVITATION',
                payload: invitationPayload,
                createdAt: Date.now(),
                isRead: true,
            };
            sendStructuredMessage(activeThreadId, msg);
        } else if (action === 'REQUEST_TRIAL') {
            if (!activeThreadId) return;
            const invite = payload as any;
            setTrialDialogDefaultBriefId(invite?.briefId ? String(invite.briefId) : undefined);
            setIsTrialDialogOpen(true);
        } else if (action === 'OPEN_PROPOSAL_DIALOG') {
            setProposalType('SUBMIT');
            setProposalInitialData(undefined);
            setIsProposalDialogOpen(true);
        } else if (action === 'OPEN_TRIAL_REQUEST_DIALOG') {
            setTrialDialogDefaultBriefId(activeThread?.category === 'BRIEF' ? activeThread.referenceId : undefined);
            setIsTrialDialogOpen(true);
        } else if (action === 'EDIT_PROPOSAL') {
            setProposalType('SUBMIT');
            setProposalInitialData(payload);
            setProposalReadOnly(false);
            setIsProposalDialogOpen(true);
        } else if (action === 'SEND_PROPOSAL_REQUEST_MSG') {
            setProposalType('REQUEST');
            setProposalInitialData(undefined);
            setIsProposalDialogOpen(true);
        } else if (action === 'VIEW_PROPOSAL') {
            setProposalType('SUBMIT');
            setProposalInitialData(payload);
            setProposalReadOnly(true);
            setIsProposalDialogOpen(true);
        } else if (action === 'INVITE_COOPERATION_FROM_PROPOSAL') {
            if (!activeThreadId) return;
            const proposal = payload as ProposalPayload;
            const invitationPayload: InvitationPayload = {
                text: '你的方案很棒，我们开始合作吧，请起草一份合同。',
                title: `合作方案参考`,
                description: proposal?.description,
                budgetRange: proposal ? `${proposal.currency || 'CNY'} ${proposal.budget?.toLocaleString()}` : undefined,
                tags: [],
            } as any;
            const newMessage: ThreadMessage = {
                id: `msg_${Date.now()}`,
                threadId: activeThreadId,
                senderId: actualCurrentUserId,
                content: '请求合同',
                type: 'INVITATION',
                payload: invitationPayload,
                createdAt: Date.now(),
                isRead: true,
            };
            sendStructuredMessage(activeThreadId, newMessage);
        } else if (action === 'INVITE_COOPERATION') {
            // Quick Invite Action
            if (!activeThreadId) return;
            const thread = threads.find(t => t.id === activeThreadId);
            const invitationPayload: InvitationPayload = {
                text: "我对您的方案很感兴趣，我们可以签署合同开始合作吗？",
                briefId: thread?.category === 'BRIEF' ? thread.referenceId : undefined,
                serviceId: thread?.category === 'SERVICE' ? thread.referenceId : undefined,
            };
            const newMessage: ThreadMessage = {
                id: `msg_${Date.now()}`,
                threadId: activeThreadId,
                senderId: actualCurrentUserId,
                content: '发起合作邀请',
                type: 'INVITATION',
                payload: invitationPayload,
                createdAt: Date.now(),
                isRead: true
            };

            sendStructuredMessage(activeThreadId, newMessage);
        } else if (action === 'RESPOND_TRIAL_REQUEST') {
            if (!activeThreadId) return;
            const responsePayload = payload as { messageId: string; status: TrialRequestStatus; briefId: string };

            if (responsePayload.status === 'ACCEPTED') {
                const trialReqPayload = (responsePayload as any).trialRequestPayload;
                const thread = threads.find(t => t.id === activeThreadId);

                if (trialReqPayload?.trialId) {
                    const clientParticipant = thread?.participants.find((participant) => participant.role === 'CLIENT');
                    const creatorParticipant = thread?.participants.find((participant) => participant.role === 'CREATOR');

                    if (!clientParticipant?.userId || !creatorParticipant?.userId) {
                        updateTrialRequestStatus(activeThreadId, responsePayload.messageId, 'PENDING');
                        showToast('试稿已接受，但无法识别客户与创作者身份', 'error');
                        return;
                    }

                    try {
                        const { trialService } = await import('../../services/trialService');
                        const createTrialRes = await trialService.createTrial({
                            trialId: trialReqPayload.trialId,
                            briefId: trialReqPayload.briefId,
                            clientUserId: clientParticipant.userId,
                            creatorUserId: creatorParticipant.userId,
                            initiatedByUserId: trialReqPayload.initiatedByUserId,
                            trialFeeAmount: trialReqPayload.trialFeeAmount,
                            reviewSlaHours: trialReqPayload.reviewSlaHours,
                        });

                        if (!createTrialRes.success) {
                            updateTrialRequestStatus(activeThreadId, responsePayload.messageId, 'PENDING');
                            showToast(createTrialRes.error || '试稿创建失败', 'error');
                            return;
                        }
                    } catch (error) {
                        console.error('Failed to create real trial record', error);
                        updateTrialRequestStatus(activeThreadId, responsePayload.messageId, 'PENDING');
                        showToast('试稿已接受，但创建试稿记录失败', 'error');
                        return;
                    }
                } else {
                    updateTrialRequestStatus(activeThreadId, responsePayload.messageId, 'PENDING');
                    showToast('试稿请求缺少试稿标识，无法创建记录', 'error');
                    return;
                }

                updateTrialRequestStatus(activeThreadId, responsePayload.messageId, responsePayload.status);
                const systemMessage: ThreadMessage = {
                    id: `trial_status_${Date.now()}`,
                    threadId: activeThreadId,
                    senderId: actualCurrentUserId,
                    content: trialReqPayload?.trialFeeAmount
                        ? '已接受试稿，请需求方完成支付后开始试稿。'
                        : '已接受试稿，试稿已开始。',
                    type: 'SYSTEM',
                    payload: {
                        event: 'TRIAL_REQUEST_STATUS_UPDATED',
                        trialId: trialReqPayload.trialId,
                        briefId: responsePayload.briefId,
                        trialRequestStatus: 'ACCEPTED'
                    } as any,
                    createdAt: Date.now(),
                    isRead: true
                };
                sendStructuredMessage(activeThreadId, systemMessage);

                await briefService.incrementTrialCount(responsePayload.briefId)
                    .then(() => {
                        window.dispatchEvent(new CustomEvent('briefs_updated'));
                        showToast(trialReqPayload?.trialFeeAmount ? '试稿已接受，等待需求方支付' : '试稿已开始', 'success');
                    })
                    .catch((error) => {
                        console.error('Increment brief trial count failed:', error);
                        showToast('试稿已开始，但在服务器总数更新中出错', 'error');
                    });
            } else {
                updateTrialRequestStatus(activeThreadId, responsePayload.messageId, responsePayload.status);
                const systemMessage: ThreadMessage = {
                    id: `trial_status_${Date.now()}`,
                    threadId: activeThreadId,
                    senderId: actualCurrentUserId,
                    content: '已拒绝试稿请求。',
                    type: 'SYSTEM',
                    payload: {
                        event: 'TRIAL_REQUEST_STATUS_UPDATED',
                        trialId: (responsePayload as any).trialRequestPayload?.trialId,
                        briefId: responsePayload.briefId,
                        trialRequestStatus: 'REJECTED'
                    } as any,
                    createdAt: Date.now(),
                    isRead: true
                };
                sendStructuredMessage(activeThreadId, systemMessage);
                showToast('已拒绝试稿请求', 'success');
            }
        } else if (action === 'CLEAR_HISTORY') {
            if (!activeThreadId) return;
            const result = await confirm({
                title: '清空聊天记录',
                message: '确定清空当前聊天记录？该操作只会清空本地 IM 历史。',
                confirmText: '清空',
                cancelText: '取消',
                tone: 'danger'
            });
            if (result.action !== 'confirm') return;
            timService.clearHistory(activeThreadId).then(() => {
                setMessages([]);
                showToast('聊天记录已清空', 'success');
            }).catch(() => showToast('清空失败', 'error'));
        } else if (action === 'HANDOFF_TO_OFFICIAL') {
            if (!activeThreadId || !activeThreadPartner?.userId) return;
            const result = await confirm({
                title: '接入官方',
                message: `当前与 ${activeThreadPartner.nickname || activeThreadPartner.userId} 的聊天记录原文会交给官方用户查看。`,
                confirmText: '接入官方',
                cancelText: '取消'
            });
            if (result.action !== 'confirm') return;

            try {
                const transcript = await buildTranscriptFromThread(activeThreadId);
                if (transcript.length === 0) {
                    showToast('当前会话没有可转交的聊天记录', 'error');
                    return;
                }
                const handoff = await chatApiService.createHandoff({
                    clientUserId: activeThreadPartner.userId,
                    sourceConversationId: activeThreadId,
                    transcript
                });
                appendMessageToThread(activeThreadId, {
                    id: `handoff_notice_${handoff.handoffId}`,
                    threadId: activeThreadId,
                    senderId: actualCurrentUserId,
                    content: '已接入官方，官方用户将继续跟进沟通。',
                    type: 'SYSTEM',
                    payload: {
                        event: 'OFFICIAL_HANDOFF_SOURCE_NOTICE',
                        handoffId: handoff.handoffId,
                        officialUserId: handoff.officialUserId
                    } as any,
                    createdAt: Date.now(),
                    isRead: true
                });
                setHandoffs((prev) => [handoff, ...prev.filter((item) => item.handoffId !== handoff.handoffId)]);
                showToast('已接入官方', 'success');
            } catch (error: any) {
                console.error('Failed to hand off chat:', error);
                showToast(error.message || '接入官方失败', 'error');
            }
        } else if (action === 'RETURN_TO_CREATOR') {
            const handoff = (payload || activeHandoff) as ChatHandoff | undefined;
            if (!handoff) return;
            const result = await confirm({
                title: '交回创作者',
                message: '原对话只会收到交回提示，不会带上官方沟通记录。',
                confirmText: '交回',
                cancelText: '取消',
                inputLabel: '给创作者的备注（可选）',
                inputPlaceholder: '例如：已确认需求方向，请继续跟进合同细节。',
                inputMaxLength: 500
            });
            if (result.action !== 'confirm') return;

            try {
                const returnedHandoff = await chatApiService.returnHandoff(handoff.handoffId, result.value?.trim());
                setHandoffs((prev) => prev.map((item) => item.handoffId === returnedHandoff.handoffId ? returnedHandoff : item));
                showToast('已交回创作者', 'success');
            } catch (error: any) {
                console.error('Failed to return handoff:', error);
                showToast(error.message || '交回创作者失败', 'error');
            }
        } else if (action === 'VIEW_CONTRACT') {
            navigate(`/contracts/${payload}`);
        } else if (action === 'VIEW_BRIEF') {
            const briefId = payload;
            import('../../services/briefService').then(({ briefService }) => {
                briefService.getBriefById(briefId).then((res) => {
                    const payload = res.data as any;
                    const detail = payload?.data ?? payload;
                    if (res.success && detail?.brief) {
                        setSelectedBrief(detail.brief);
                        setIsBriefModalOpen(true);
                    }
                }).catch((error) => {
                    console.error('Failed to load related brief:', error);
                    showToast('加载相关需求失败', 'error');
                });
            });
        } else if (action === 'VIEW_TRIAL') {
            const trialId = String(payload || '');
            if (!trialId) return;

            const { trialService } = await import('../../services/trialService');
            const trialRes = await trialService.getTrialById(trialId);

            if (trialRes.success) {
                navigate(`/trials/${trialId}`);
                return;
            }

            if (trialRes.statusCode === 404 && activeThreadId) {
                const relatedTrialMessage = messages.find((message) => (
                    message.type === 'TRIAL_REQUEST'
                    && (message.payload as TrialRequestPayload | undefined)?.trialId === trialId
                ));

                if (relatedTrialMessage) {
                    updateTrialRequestStatus(activeThreadId, relatedTrialMessage.id, 'PENDING');
                }

                showToast('试稿记录尚未创建成功，已恢复为待处理，请重新接受试稿', 'error');
                return;
            }

            showToast(trialRes.error || '无法获取试稿信息', 'error');
        } else if (action === 'VIEW_SERVICE') {
            const serviceId = payload;
            import('../../services/serviceService').then(({ serviceService }) => {
                serviceService.getServiceById(serviceId).then((res) => {
                    const payload = res.data as any;
                    const detail = payload?.data ?? payload;
                    if (res.success && detail?.service) {
                        setSelectedService(detail.service);
                        setIsServiceModalOpen(true);
                        return;
                    }
                    throw new Error('Service not found in api');
                }).catch((error) => {
                    console.error('Failed to load related service:', error);
                    import('../Profile/grids/ServiceGrids').then(({ MOCK_SERVICES }) => {
                        const fallbackService = MOCK_SERVICES.find((service) => String(service.id) === String(serviceId));
                        if (fallbackService) {
                            setSelectedService(fallbackService);
                            setIsServiceModalOpen(true);
                            return;
                        }
                        showToast('加载相关服务失败', 'error');
                    }).catch((fallbackError) => {
                        console.error('Failed to load fallback related service:', fallbackError);
                        showToast('加载相关服务失败', 'error');
                    });
                });
            });
        }
    };

    const handleProposalSubmit = (payload: ProposalPayload) => {
        if (!activeThreadId) return;

        let newMessage: ThreadMessage;

        if (proposalType === 'SUBMIT') {
            newMessage = {
                id: `msg_${Date.now()}`,
                threadId: activeThreadId,
                senderId: actualCurrentUserId,
                content: '我提交了一份合作方案，请查收。',
                type: 'PROPOSAL',
                payload: payload,
                createdAt: Date.now(),
                isRead: true
            };
        } else {
            // Map Request to Invitation
            const invitationPayload: InvitationPayload = {
                text: `希望您能为这个需求提交一份方案：${payload.description}`,
                briefId: payload.sourceBriefId || undefined,
                isProposalRequest: true,
            } as any;

            newMessage = {
                id: `msg_${Date.now()}`,
                threadId: activeThreadId,
                senderId: actualCurrentUserId,
                content: '请求方案',
                type: 'INVITATION',
                payload: invitationPayload,
                createdAt: Date.now(),
                isRead: true
            };
        }

        sendStructuredMessage(activeThreadId, newMessage);
    };

    const trialBriefOwnerUserId = activeThreadCurrentUserRole === 'CLIENT'
        ? actualCurrentUserId
        : activeThreadPartner?.userId;
    const trialRequesterLabel = activeThreadCurrentUserRole === 'CLIENT' ? '自己的需求' : '客户已有的需求';
    const defaultTrialBriefId = activeThread?.category === 'BRIEF' ? activeThread.referenceId : undefined;
    const resolvedTrialDialogDefaultBriefId = trialDialogDefaultBriefId || defaultTrialBriefId;

    // Mobile layout rule: show EITHER the thread list OR the chat, never
    // both. Desktop (md+) shows both side-by-side as before. Toggling is
    // driven entirely by activeThreadId, which is in turn driven by the
    // URL param (/messages vs /messages/:id) — so browser back works
    // naturally on mobile.
    const showListOnMobile = !activeThreadId;
    const showChatOnMobile = !!activeThreadId;

    return (
        <div className="h-screen bg-white pt-20 pb-0 flex overflow-hidden">
            <ToastWrapper />
            <ConfirmWrapper />
            <div className="max-w-[1440px] mx-auto w-full flex border-x border-gray-100 shadow-sm relative">

                {/* Sidebar — full-width on mobile when no thread is open,
                    hidden when one is. Fixed 320px on md+ as before. */}
                <div className={`${showListOnMobile ? 'flex' : 'hidden'} md:flex w-full md:w-80 md:flex-shrink-0 md:border-r border-gray-100 flex-col bg-white z-10`}>
                    <div className="p-6 border-b border-gray-100 flex flex-col items-start">
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 group w-fit"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            <span className="text-sm font-medium">返回</span>
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            <MessageSquare className="w-6 h-6 text-blue-600" />
                            消息中心
                        </h1>
                    </div>
                    <ThreadList
                        threads={threads}
                        activeThreadId={activeThreadId || undefined}
                        onSelect={handleSelectThread}
                        currentUserId={actualCurrentUserId}
                    />
                </div>

                {/* Chat Area — hidden on mobile when no thread is open. */}
                <div className={`${showChatOnMobile ? 'flex' : 'hidden'} md:flex flex-1 min-w-0 bg-white relative flex-col`} onClick={() => {
                    if (activeThreadId && activeThreadId !== SYSTEM_THREAD_ID) {
                        timService.setMessageRead(activeThreadId).catch(console.error);
                    }
                }}>
                    {/* Mobile-only back-to-list bar so users can return to
                        the conversation list. Hidden on md+ where the
                        side-by-side layout makes a back button redundant. */}
                    {activeThreadId && (
                        <div className="md:hidden flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-white">
                            <button
                                onClick={() => navigate('/messages', { replace: true })}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                                aria-label="返回会话列表"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span className="text-sm font-medium">会话列表</span>
                            </button>
                        </div>
                    )}
                    <div className="flex-1 min-h-0">
                        {activeThreadId ? (
                            activeThreadId === SYSTEM_THREAD_ID ? (
                                <SystemChatWindow />
                            ) : activeThread ? (
                                <ChatWindow
                                    thread={activeThread}
                                    messages={messages}
                                    currentUserId={actualCurrentUserId}
                                    currentUserName={currentUserName}
                                    currentUserAvatar={currentUserAvatar}
                                    currentUserRole={currentUserRole}
                                    isCurrentUserOfficial={isCurrentUserOfficial}
                                    officialUserId={officialUser?.userId}
                                    handoff={activeHandoff}
                                    transcript={activeHandoff ? handoffTranscripts[activeHandoff.handoffId] : undefined}
                                    isTranscriptLoading={activeHandoff ? loadingTranscriptIds.has(activeHandoff.handoffId) : false}
                                    onLoadTranscript={loadHandoffTranscript}
                                    onSendMessage={handleSendMessage}
                                    onAction={handleAction}
                                />
                            ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                    未找到该对话
                                </div>
                            )
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                    <MessageSquare className="w-8 h-8 text-gray-300" />
                                </div>
                                <p>选择一个对话开始沟通</p>
                            </div>
                        )}
                    </div>
                </div>


            </div>

            <TrialRequestDialog
                isOpen={isTrialDialogOpen}
                onClose={() => {
                    setIsTrialDialogOpen(false);
                    setTrialDialogDefaultBriefId(undefined);
                }}
                onSubmit={handleTrialRequestSubmit}
                briefOwnerUserId={trialBriefOwnerUserId}
                defaultBriefId={resolvedTrialDialogDefaultBriefId}
                requesterLabel={trialRequesterLabel}
                canSetPaidTrialAmount={activeThreadCurrentUserRole === 'CLIENT'}
            />

            <ProposalDialog
                isOpen={isProposalDialogOpen}
                onClose={() => {
                    setIsProposalDialogOpen(false);
                    setProposalInitialData(undefined);
                    setProposalReadOnly(false);
                }}
                onSubmit={handleProposalSubmit}
                type={proposalType}
                readOnly={proposalReadOnly}
                initialData={proposalInitialData}
                currentUserId={actualCurrentUserId}
            />

            <CreateContractModal
                isOpen={isContractModalOpen}
                onClose={() => setIsContractModalOpen(false)}
                onSubmit={async (data, isDraft: boolean = false) => {
                    try {
                        const payload = { ...data, status: isDraft ? 'DRAFT' : 'PENDING_CLIENT_APPROVAL' } as any;
                        const res = await contractService.createContract(payload);
                        if (res.success) {
                            const raw = res.data as any;
                            const contract = raw?.data?.contract || raw?.contract || raw;
                            const contractId = contract?.contract_id || contract?.contractId || contract?.id;

                            showToast(isDraft ? '草稿已保存' : '合同创建成功，已发送给客户审核', 'success');
                            setIsContractModalOpen(false);
                            // Send contract message to client
                            if (!isDraft && activeThreadId && contractId) {
                                const contractMsg: ThreadMessage = {
                                    id: `msg_${Date.now()}`,
                                    threadId: activeThreadId,
                                    senderId: actualCurrentUserId,
                                    content: '我发起了一份合作合同，请查看并确认。',
                                    type: 'SYSTEM',
                                    payload: { contractId, title: data.title || '合作合同' } as any,
                                    createdAt: Date.now(),
                                    isRead: true,
                                };
                                sendStructuredMessage(activeThreadId, contractMsg);
                            }
                        } else {
                            showToast(res.error || (isDraft ? '保存草稿失败' : '创建合同失败'), 'error');
                        }
                    } catch (error) {
                        console.error('Create contract error:', error);
                        showToast(isDraft ? '保存草稿失败' : '创建合同失败，请稍后重试', 'error');
                    }
                }}

                initialData={contractInitialData}
            />

            {selectedBrief && (
                <BriefDetailModal
                    isOpen={isBriefModalOpen}
                    onClose={() => {
                        setIsBriefModalOpen(false);
                        setSelectedBrief(null);
                    }}
                    brief={selectedBrief}
                    isOwnProfile={false}
                />
            )}

            {selectedService && (
                <ServiceDetailModal
                    isOpen={isServiceModalOpen}
                    onClose={() => {
                        setIsServiceModalOpen(false);
                        setSelectedService(null);
                    }}
                    service={selectedService}
                    isOwnProfile={false}
                />
            )}
        </div>
    );
};

export default Messages;
